import React, { useEffect, useState } from "react";
import Image from "next/image";
import { useRouter } from "next/router";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Tooltip from "@mui/material/Tooltip";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

import SVG from "@/assets/svg";
import { Visit, VisitStatus } from "@/interfaces/IServiceUser";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setSelectedVisit } from "@/redux/reducers/roster";
import { $public } from "@/services";
import { formatDateWithTime, getSanitizedHtml } from "@/utils/helpers";
import logger from "@/runtime-utils/observability/logger";
import CostumeButton from "../button";
import ListView from "../list";
import { DrawerStyled } from "./drawerStyled";
import { useSocket } from "@/context";
import { getSheduleVisitById } from "@/services/service-users/schedule-visit";

dayjs.extend(utc);

function DailyNotes() {
  const dispatch = useAppDispatch();
  const router = useRouter();
  const socket = useSocket();
  const { selectedVisit } = useAppSelector((state) => state.roster);

  logger.debug("selectedVisit", selectedVisit);

  const [visit, setVisit] = useState<Visit>(
    selectedVisit?.extendedProps as Visit
  );

  useEffect(() => {
    if (!socket) return;

    const handleRefreshVisitById = async (visitId?: string | null) => {
      try {
        const selectedVisitId =
          selectedVisit?.extendedProps?._id ??
          selectedVisit?.extendedProps?.id ??
          visit?._id ??
          null;

        if (
          visitId != null &&
          selectedVisitId != null &&
          String(visitId).toLowerCase() ===
            String(selectedVisitId).toLowerCase()
        ) {
          const { data } = await getSheduleVisitById(visitId);
          setVisit(data);
        }
      } catch (error) {
        logger.error("Error fetching visit by ID:", error);
      }
    };

    socket.on("visit:refreshVisitById", handleRefreshVisitById);

    return () => {
      socket.off("visit:refreshVisitById", handleRefreshVisitById);
    };
  }, [socket]);

  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const [selectedTaskId, setSelectedTaskId] = React.useState<string | null>(
    null
  );

  const creationDisabled = [
    VisitStatus.CANCELLED,
    // VisitStatus.COMPLETED,
    VisitStatus.UN_ALLOCATED,
  ].includes(visit?.status as VisitStatus);

  const handleClose = () => {
    setSelectedTaskId(null);
    setAnchorEl(null);
  };

  const handleView = () => {
    router.push(`/dailyNote/${encodeURIComponent(selectedTaskId || "")}`);
    setAnchorEl(null);
    dispatch(setSelectedVisit(null));
  };

  return (
    <DrawerStyled>
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          padding: "10px 5px 0 0",
        }}
      >
        <Tooltip
          arrow
          title={
            creationDisabled ? `Visit is ${visit.status || "unassigned"}` : ""
          }
        >
          <CostumeButton
            onClick={() => {
              router.push({
                pathname: "/dailyNote/add-note",
                query: {
                  visit: visit?._id,
                  serviceUser: JSON.stringify({
                    _id: visit?.serviceUser?._id,
                    name: visit?.serviceUser?.name,
                  }),
                  rosterDate: selectedVisit?.startStr,
                },
              });
              // dispatch(setSelectedVisit(null));
            }}
            // disabled={creationDisabled}
            // disable the button if selectedVisit?.start is in the future
            disabled={dayjs(selectedVisit?.start).utc().isAfter(dayjs(), "day")}
            className="costume"
          >
            Add daily note
            <SVG.Addfile />
          </CostumeButton>
        </Tooltip>
      </Box>
      <ListView
        // data={visit?.notes || []}

        data={(visit?.notes || [])
          .slice()
          .sort(
            (a, b) =>
              dayjs(b.createdAt).valueOf() - dayjs(a.createdAt).valueOf()
          )}
        renderItem={(item) => {
          const [hour, minute] = item.taskTime.split(":");
          const taskDate = dayjs(item.taskDate)
            .set("hour", Number(hour))
            .set("minute", Number(minute));

          const isSame = dayjs(item?.createdAt).isSame(
            dayjs(taskDate),
            "minute"
          );

          const isSameTime =
            isSame ||
            formatDateWithTime(item?.createdAt) ===
              formatDateWithTime(taskDate);

          return (
            <Box className="Detail">
              <Box className="daily-note-card-row">
                <Box className="daily-note-card-main">
                  <Box className="daily-note-task-icons">
                    {item?.task?.length > 2 ? (
                      <Box sx={{ position: "relative" }}>
                        {item?.task
                          ?.slice(0, 1)
                          .map((task: any, index: number) => (
                            <Box key={index} className="daily-notes">
                              <Image
                                src={$public(task.logo)}
                                alt={""}
                                width={25}
                                height={25}
                              />
                            </Box>
                          ))}
                        <Box className="task-counter">
                          +{item?.task?.length - 1}
                        </Box>
                      </Box>
                    ) : (
                      <Box className="daily-note-icon-stack">
                        {item?.task
                          ?.slice(0, 2)
                          .map((task: any, index: number) => (
                            <Box
                              key={index}
                              className={`daily-note-${index + 1}`}
                            >
                              <Image
                                src={$public(task.logo)}
                                alt={""}
                                width={25}
                                height={25}
                              />
                            </Box>
                          ))}
                      </Box>
                    )}
                  </Box>
                  <Box className="daily-note-card-copy">
                    <p style={{ color: "#000000" }} className="username">
                      {item.createdBy.name}
                    </p>
                    <p className="visit">
                      {item?.task?.length > 2
                        ? `${item?.task[0]?.title} and more..`
                        : item?.task.map((task: any) => task?.title).join(", ")}
                    </p>
                    <p
                      className="detail"
                      style={{ color: "#000000" }}
                      dangerouslySetInnerHTML={{
                        __html: getSanitizedHtml(item?.description),
                      }}
                    />
                    <p className="user-detail">
                      Task Date: {formatDateWithTime(taskDate)}
                    </p>
                    {item?.isDeleted?.value ? (
                      <p className="user-detail">
                        Revised by {item?.updatedBy?.name} on{" "}
                        {formatDateWithTime(item?.updatedAt)}
                      </p>
                    ) : item.updatedBy ? (
                      <p className="user-detail">
                        Revised by {item?.updatedBy?.name} on{" "}
                        {formatDateWithTime(item?.updatedAt)}
                      </p>
                    ) : null}
                  </Box>
                </Box>

                <Box className="daily-note-card-actions">
                  <SVG.DotsHorizontal
                    width="20px"
                    style={{ cursor: "pointer" }}
                    onClick={(e: React.MouseEvent<HTMLElement>) => {
                      setSelectedTaskId(item._id);
                      setAnchorEl(e.currentTarget);
                    }}
                  />
                </Box>
              </Box>
            </Box>
          );
        }}
      />

      <Menu
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
      >
        <MenuItem onClick={handleView}>Edit Note</MenuItem>
      </Menu>
    </DrawerStyled>
  );
}

export default DailyNotes;
